import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { SnackAlertContext } from "../../../../context/SnackAlertContext";
import useVariables from "../../../../hooks/useVariables";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import SelectCustomer from "../../Quotes/QuoteForm/SelectCustomer";
import AddSalesTicketItemDialog from "./AddSalesTicketItemDialog";
import { currencyFormat } from "../../../../components/NewDetail/calculations/utils";
import { Add, Close } from "@mui/icons-material";

const EMPTY_SALES_TICKET = {};

const SalesTicketForm = ({ loading, onSave }) => {
  const theme = useTheme();
  const { ezorder, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [addSalesTicketItemDialogOpen, setAddSalesTicketItemDialogOpen] =
    useState(false);

  const formValidationSchema = Yup.object({
    // company: Yup.object().nullable().required("Please select a customer"),
    salesTicketItems: Yup.array().of(
      Yup.object().shape({
        consumable: Yup.object()
          .shape({
            id: Yup.number().required(`Missing product selection`),
          })
          .required(`Missing product selection`),
        description: Yup.string().required(`Missing product description`),
        uom: Yup.string().required(`Missing product unit of measure`),
        unitPrice: Yup.number()
          .min(0, "Item Unit price cannot be negative")
          .required("Item Unit price required"),
        totalPrice: Yup.number() // no tax
          .min(0, "Item Total price cannot be negative")
          .required("Item Total price required"),
        quantity: Yup.number()
          .positive("Item quantity must be greater than zero")
          .required("Item quantity required"),
      })
    ),

    subTotalPrice: Yup.number().required("Subtotal Price Required"),
    salesTaxRate: Yup.number().required("Sales Tax Rate Required"),
    totalSalesTax: Yup.number().required("Total Sales Tax Required"),
    totalPrice: Yup.number().required("Total Price Required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      isNotAnIndustryCustomer: true,
      company: null,
      salesTicketItems: [],
      subTotalPrice: 0,
      salesTaxRate: 0.091, // 9.1%
      totalSalesTax: 0,
      totalPrice: 0,
    },
    validationSchema: formValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  return (
    <Box>
      <Box sx={{ marginTop: "16px" }}>
        <Typography variant="h6">Details</Typography>
        {/* Select Company */}
        <Box sx={{ marginTop: "16px" }}>
          <SelectCustomer
            disabled={formik.values.isNotAnIndustryCustomer}
            value={formik.values.company}
            onUpdate={(company) => {
              formik.setFieldValue("company", company);
            }}
            label="Customer"
            enableAdd={true}
            onBlur={formik.handleBlur}
            error={formik.errors.company && Boolean(formik.errors.company)}
            helperText={formik.errors.company && `${formik.errors.company}`}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formik.values.isNotAnIndustryCustomer}
              onChange={(e) => {
                formik.setValues({
                  ...formik.values,
                  company: null,
                  isNotAnIndustryCustomer: e.target.checked,
                });
              }}
            />
          }
          label="Not an Industry customer"
        />
      </Box>
      {/* Add Items */}
      <Box sx={{ marginTop: "16px" }}>
        <Typography variant="h6">Items</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">Description</TableCell>
                <TableCell align="left">UOM</TableCell>
                <TableCell align="left">Quantity</TableCell>
                <TableCell align="left">Price</TableCell>
                <TableCell align="left">Total Price</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formik &&
                formik.values &&
                formik.values.salesTicketItems &&
                formik.values.salesTicketItems.map((salesTicketItem, index) => {
                  let itemError = formik.errors.salesTicketItems
                    ? formik.errors.salesTicketItems[index]
                    : null;
                  return (
                    <TableRow key={index}>
                      <TableCell align="left">
                        {salesTicketItem.description}
                      </TableCell>
                      <TableCell align="left">{salesTicketItem.uom}</TableCell>
                      <TableCell align="left">
                        <TextField
                          type="number"
                          size="small"
                          sx={{ marginTop: "8px", marginRight: "8px" }}
                          label="Quantity"
                          value={salesTicketItem.quantity}
                          onChange={(e) => {
                            // UPDATE BOTH QUANTITY AND TOTAL PRICE
                            let salesTicketItemsCopy = [
                              ...formik.values.salesTicketItems,
                            ];
                            salesTicketItemsCopy[index].quantity =
                              e.target.value;
                            salesTicketItemsCopy[index].totalPrice =
                              Number(e.target.value) *
                              Number(salesTicketItemsCopy[index].unitPrice);

                            let subTotalPrice = 0;
                            for (let item of salesTicketItemsCopy) {
                              subTotalPrice += Number(item.totalPrice);
                            }
                            let totalSalesTax =
                              subTotalPrice * formik.values.salesTaxRate;
                            let totalPrice = subTotalPrice + totalSalesTax;
                            formik.setValues((prev) => ({
                              ...prev,
                              salesTicketItems: salesTicketItemsCopy,

                              subTotalPrice,
                              totalSalesTax,
                              totalPrice,
                            }));
                          }}
                          //   onBlur={formik.handleBlur}
                          error={
                            itemError &&
                            itemError.quantity &&
                            Boolean(itemError.quantity)
                          }
                          helperText={
                            itemError &&
                            itemError.quantity &&
                            `${itemError.quantity}`
                          }
                          onFocus={(e) => {
                            e.target.select();
                          }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <TextField
                          type="number"
                          size="small"
                          sx={{ marginTop: "8px", marginRight: "8px" }}
                          label="Unit Price"
                          value={salesTicketItem.unitPrice}
                          onChange={(e) => {
                            // UPDATE BOTH UNIT PRICE AND TOTAL PRICE
                            let salesTicketItemsCopy = [
                              ...formik.values.salesTicketItems,
                            ];
                            salesTicketItemsCopy[index].unitPrice =
                              e.target.value;
                            salesTicketItemsCopy[index].totalPrice =
                              Number(e.target.value) *
                              Number(salesTicketItemsCopy[index].quantity);

                            let subTotalPrice = 0;
                            for (let item of salesTicketItemsCopy) {
                              subTotalPrice += Number(item.totalPrice);
                            }

                            let totalSalesTax =
                              subTotalPrice * formik.values.salesTaxRate;
                            let totalPrice = subTotalPrice + totalSalesTax;
                            formik.setValues((prev) => ({
                              ...prev,
                              salesTicketItems: salesTicketItemsCopy,
                              subTotalPrice,
                              totalSalesTax,
                              totalPrice,
                            }));
                          }}
                          //   onBlur={formik.handleBlur}
                          error={
                            itemError &&
                            itemError.unitPrice &&
                            Boolean(itemError.unitPrice)
                          }
                          helperText={
                            itemError &&
                            itemError.unitPrice &&
                            `${itemError.unitPrice}`
                          }
                          onFocus={(e) => {
                            e.target.select();
                          }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {currencyFormat(salesTicketItem.totalPrice ?? 0)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            let salesTicketItemsCopy = [
                              ...formik.values.salesTicketItems,
                            ];
                            salesTicketItemsCopy.splice(index, 1);
                            // formik.setFieldValue(
                            //   "salesTicketItems",
                            //   salesTicketItemsCopy
                            // );

                            let subTotalPrice = 0;
                            for (let item of salesTicketItemsCopy) {
                              subTotalPrice += Number(item.totalPrice);
                            }

                            let totalSalesTax =
                              subTotalPrice * formik.values.salesTaxRate;
                            let totalPrice = subTotalPrice + totalSalesTax;
                            formik.setValues((prev) => ({
                              ...prev,
                              salesTicketItems: salesTicketItemsCopy,
                              subTotalPrice,
                              totalSalesTax,
                              totalPrice,
                            }));
                          }}
                        >
                          <Close />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              <TableRow>
                <TableCell align="center" colSpan={20}>
                  <Button
                    onClick={() => {
                      setAddSalesTicketItemDialogOpen(true);
                    }}
                  >
                    <Add />
                    Add Item
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box>
        <Typography variant="h5">
          Subtotal: {currencyFormat(formik.values.subTotalPrice)}
        </Typography>
        <Typography variant="h5">
          Sales Tax: {currencyFormat(formik.values.totalSalesTax)}
        </Typography>
        <Typography variant="h5">
          Total: {currencyFormat(formik.values.totalPrice)}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}
      >
        <LoadingButton variant="outlined">Cancel</LoadingButton>
        <LoadingButton
          variant="contained"
          sx={{ marginLeft: "8px" }}
          onClick={() => {
            formik.submitForm();
          }}
        >
          Save
        </LoadingButton>
      </Box>
      <AddSalesTicketItemDialog
        open={addSalesTicketItemDialogOpen}
        onClose={() => {
          setAddSalesTicketItemDialogOpen(false);
        }}
        onAdd={(consumable) => {
          formik.setFieldValue("salesTicketItems", [
            ...formik.values.salesTicketItems,
            {
              consumable: consumable,
              description: consumable.description, // copy
              uom: consumable.uom, // copy
              unitPrice: consumable.retailPrice, // copy
              quantity: 0,
              totalPrice: 0,
            },
          ]);
          setAddSalesTicketItemDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default SalesTicketForm;
